import { Navigate } from "react-router-dom";
import { useGetIcmStatementDetail, useGetIcmStatements } from "@/icm/query";
import { isPayPeriodLocked } from "@/payroll/utils/period-helpers";
import { TechnicianStatementExperience } from "./technician-statement-experience";
import { TechnicianWorkbookExperience } from "./technician-workbook-experience";
import { useTechnicianPayrollBase } from "./use-technician-payroll-base";
import { getStatementOwnerKey } from "./utils";

export const TechnicianPayrollPage = () => {
  const {
    businessId,
    techUser,
    isUserLoading,
    sortedPayPeriods,
    periodOptions,
    periodStart,
    onPeriodChange,
    isPayPeriodsLoading,
  } = useTechnicianPayrollBase();

  const selectedPeriod = sortedPayPeriods.find(
    (period) => period.start_date === periodStart,
  );

  const selectedAiRunId =
    selectedPeriod?.latest_completed_ai_script_run_id ?? null;
  const isLocked = isPayPeriodLocked(selectedPeriod);
  const selectedIcmRunId = selectedPeriod?.latest_completed_icm_run_id ?? null;
  const shouldLoadIcmStatement = !selectedAiRunId;
  const { data: statements = [], isLoading: isStatementsLoading } =
    useGetIcmStatements(
      businessId,
      shouldLoadIcmStatement ? selectedIcmRunId : null,
    );

  const techCrmExternalId = techUser?.crm_external_id;
  const myStatement = techCrmExternalId
    ? (statements.find(
        (statement) => getStatementOwnerKey(statement) === techCrmExternalId,
      ) ?? null)
    : null;

  const { data: statementDetail, isLoading: isStatementLoading } =
    useGetIcmStatementDetail(
      businessId,
      shouldLoadIcmStatement ? myStatement?.id : undefined,
    );

  const isLoading =
    isUserLoading ||
    isPayPeriodsLoading ||
    isStatementsLoading ||
    isStatementLoading;

  if (!isUserLoading && !techUser) {
    return <Navigate to="/login" replace />;
  }

  if (!isLoading && sortedPayPeriods.length === 0) {
    return (
      <TechnicianStatementExperience
        statement={null}
        isLoading={false}
        periodOptions={periodOptions}
        selectedPeriodStart={periodStart}
        onPeriodChange={onPeriodChange}
        emptyTitle="No pay periods are ready yet"
        emptyDescription="Once your business publishes payroll periods, you'll be able to review your pay here."
      />
    );
  }

  if (selectedAiRunId) {
    return (
      <TechnicianWorkbookExperience
        businessId={businessId}
        runId={selectedAiRunId}
        isLocked={isLocked}
        isLoading={isUserLoading || isPayPeriodsLoading}
        periodOptions={periodOptions}
        selectedPeriodStart={periodStart}
        onPeriodChange={onPeriodChange}
      />
    );
  }

  return (
    <TechnicianStatementExperience
      statement={statementDetail ?? null}
      isLoading={isLoading}
      periodOptions={periodOptions}
      selectedPeriodStart={periodStart}
      onPeriodChange={onPeriodChange}
      emptyTitle="No statement for this pay period"
      emptyDescription="There isn't a published statement for you in this period yet. Try another pay period or send us a note if something seems off."
    />
  );
};
